import * as React from "react";
import { useState } from "react";
import { EjiService, Job } from "../../../../generated/nest-graphql";
import { hasRecordedInspection, InvoicedService } from "../../vehicleHistoryData";
import { InvoicedServiceRow } from "../../InvoicedServiceRow";
import { InspectionSummary } from "../../InspectionSummary";
import { servicedInspectionLabels, servicesByInspectionItem } from "../../inspectionRows.utils";
import { EmptySection } from "../EmptySection";
import { defaultOpenSections, JobCardSection } from "../jobCard.utils";
import { DetailsIcon, Text, TextVariant, WrenchIcon } from "../../../ui";
import { CollapsibleSection } from "./CollapsibleSection";

// The inspection marks per item whether it was invoiced, only quoted, or neither.
export const JobCardSections: React.FC<{ job: Job; services: InvoicedService[]; isMostRecent?: boolean }> = ({
  job,
  services,
  isMostRecent = false,
}) => {
  const [open, setOpen] = useState(() => defaultOpenSections(isMostRecent));
  const toggle = (section: JobCardSection) => () =>
    setOpen((current) => ({ ...current, [section]: !current[section] }));
  const checklist =
    job.preJobCheckListV2 && hasRecordedInspection(job.preJobCheckListV2) ? job.preJobCheckListV2 : null;
  return (
    <div role="group" aria-label={`Job #${job.jobNumber ?? ""} details`} className="w-full">
      <CollapsibleSection
        title="Inspection"
        Icon={DetailsIcon}
        open={open[JobCardSection.INSPECTION]}
        onToggle={toggle(JobCardSection.INSPECTION)}
      >
        {checklist ? (
          // The readings carry their own side padding; their Service / Result headings stand in for a title.
          <div className="-mx-3">
            <InspectionSummary
              checklist={checklist}
              invoiced={servicesByInspectionItem(services)}
              // Everything quoted on the job, the invoiced services included.
              quoted={servicedInspectionLabels(
                [...((job.services ?? []).filter(Boolean) as EjiService[]), ...services].map((service) => service.name)
              )}
            />
          </div>
        ) : (
          <EmptySection>No inspection recorded on this job.</EmptySection>
        )}
      </CollapsibleSection>
      <CollapsibleSection
        title="Work Done"
        Icon={WrenchIcon}
        open={open[JobCardSection.WORK_DONE]}
        onToggle={toggle(JobCardSection.WORK_DONE)}
      >
        <Text variant={TextVariant.BODY2} className="text-gray-400 pt-2">
          Invoiced services
        </Text>
        {services.length ? (
          // By position: an invoice can list the same service twice.
          services.map((service, index) => <InvoicedServiceRow key={`${index}-${service.name}`} service={service} />)
        ) : (
          <EmptySection>No invoiced services on this job.</EmptySection>
        )}
      </CollapsibleSection>
    </div>
  );
};
